import { CustomFieldDefinitionTypes, qtiTypes } from 'common/store/common/constants';
import { SearchColumn } from 'common/store/common/search/types';

import { tl } from 'common/services/i18nService';
import { MarketplaceProperties } from 'common/store/session/reducer';
import { CustomField, CustomFieldDefinition, ValueType } from '../../store/customFieldsDefinition/types';
import storeManager from '../../store';
import { CustomFieldsDefinitionState } from '../../store/customFieldsDefinition/reducer';
import customFieldsConstants from '../../features/richText/constants/CustomFieldsConstants';

export const NO_CONTENT_PLACEHOLDER = '-';

const customFieldsService = {
    specificFieldRendererWhitelist: ['Status', 'renderNumberOfItems', 'lifeCycleStatus'],
    getSaveReminder(): boolean {
        const {
            customFieldsDefinition: {
                customFieldsDefinitionKit,
            },
        } = storeManager.getState();
        const isExistUnsavedData = customFieldsDefinitionKit?.some(({ isDirty }) => isDirty);

        if (isExistUnsavedData) {
            return !confirm(tl('common.author.saveDataReminder'));
        }

        return false;
    },
    getDefinitionsKey(contentType: string): string {
        return Object.values(CustomFieldDefinitionTypes)
            .find((type: string) => contentType?.includes(type));
    },
    getEligibleReviewCustomFields(
        contentType: string,
        customFieldsDefinition: CustomFieldsDefinitionState,
        marketplaceProperties: MarketplaceProperties,
    ): Array<any> {
        const { ITEM_REVIEW_CUSTOM_FIELDS } = marketplaceProperties;
        const definitionsKey: CustomFieldDefinitionTypes = this.getDefinitionsKey(contentType);
        const definitions = definitionsKey ? customFieldsDefinition?.[definitionsKey] : [];

        if (!ITEM_REVIEW_CUSTOM_FIELDS || !contentType || !definitions?.length) {
            return [];
        }

        return definitions.filter(
            (definition: CustomFieldDefinition) => ITEM_REVIEW_CUSTOM_FIELDS.split(',').includes(definition.fieldName)
        );
    },
    getFilteredCustomFields(
        contentType: string,
        customFieldsDefinition: CustomFieldsDefinitionState,
        marketplaceProperties: MarketplaceProperties,
        reviewFieldsOnly = false,
    ): Array<any> {
        let customFields = [];

        if (reviewFieldsOnly) {
            customFields = this.getEligibleReviewCustomFields(contentType, customFieldsDefinition, marketplaceProperties);
        } else {
            customFields = this.specificFieldRendererWhitelist.map((fieldName: string) => { return { fieldName }; });
        }

        return customFields
            .sort((a: any, b: any) => {
                const nameA = a.fieldName.toUpperCase();
                const nameB = b.fieldName.toUpperCase();
                if (nameA < nameB) { return 1; }
                if (nameA > nameB) { return -1; }
                return 0;
            });
    },
    getParsedValue(newValue: any, initValue: any): ValueType {
        if (newValue.length === 0 && initValue === null) {
            return null;
        }

        if (Array.isArray(newValue)) {
            return newValue;
        }

        if (initValue !== null && newValue.length === 0) {
            return [];
        }

        return [newValue];
    },
    getCustomFieldValue(definitionId: number, selectedItem: any) {
        const customField = selectedItem?.customFields?.find((field: CustomField) => field.customFieldDefinitionId === definitionId);
        const customFieldValue = this.getValueFromCustomField(customField);
        return customField ? customFieldValue : null;
    },

    getHiddenInContent(definitionId: number, selectedItem: any): boolean | null {
        const customField = selectedItem?.customFields?.find(
            (field: CustomField) => field.customFieldDefinitionId === definitionId
        );

        return customField?.hiddenInContent ?? false;
    },

    getCustomFieldValueForCustomFieldName(customFieldName: string, customFieldsDefinition: CustomFieldsDefinitionState, currentQtiContextItem: any) {
        const itemTypeDefinition = customFieldsService.getCustomFieldDefinition(
            customFieldsDefinition[CustomFieldDefinitionTypes.QUESTION], customFieldName
        );
        const itemTypeField: CustomField = currentQtiContextItem?.customFields?.find(
            (field: CustomField) => field.customFieldDefinitionId === itemTypeDefinition?.fieldDefinitionId
        );

        return this.getValueFromCustomField(itemTypeField);
    },
    getValueFromCustomField(customField: CustomField): ValueType {
        return customField?.valueLong ?? customField?.valueDouble ?? customField?.valueText ?? customField?.valueDate ?? null;
    },
    checkSpecificRendererWhitelist(fieldName: any): boolean {
        return this.specificFieldRendererWhitelist.includes(fieldName);
    },
    getCustomFieldDefinition(objectCustomFieldDefinitions: Array<CustomFieldDefinition>, fieldName: string): CustomFieldDefinition {
        return objectCustomFieldDefinitions?.find((field: CustomFieldDefinition) => field.fieldName === fieldName);
    },
    getCustomField(customFields: Array<CustomField>, customFieldDefinition: CustomFieldDefinition): CustomField {
        return customFields.find((field: CustomField) => field.customFieldDefinitionId === customFieldDefinition.fieldDefinitionId);
    },
    getCustomFieldsAsColumnDefinitions(objectCustomFieldDefinitions: Array<any>): Array<SearchColumn> {
        return objectCustomFieldDefinitions
            .map((definition: any) => {
                return {
                    ref: definition.fieldName,
                    name: definition.fieldName,
                    type: definition.dataType,
                    fieldDefinitionId: definition.fieldDefinitionId,
                };
            });
    },
    getValueOfField(value: ValueType, asMultipleValue: boolean, fieldName?: string): ValueType {
        if (Array.isArray(value)) {
            if (fieldName === 'Item Writing Team') {
                return value.length > 0 ? value[0] : '';
            }
            if (asMultipleValue) {
                return value;
            }
            return value.join('; ');
        }
        return value;
    },
    isComplexFlowChartCombo(itemTypeValue: any, itemSubTypeValue: any, selectedItemContentType: string) {
        const isComplexFlowChartObjectiveCombo = (
            selectedItemContentType === qtiTypes.GAP_MATCH
            && itemTypeValue?.length && itemTypeValue[0] === customFieldsConstants.ITEM_FLOW_CHART_COMPLETION_OBJECTIVE
            && itemSubTypeValue?.length && itemSubTypeValue[0] === customFieldsConstants.ITEM_SUB_TYPE_COMPLEX_FLOW_CHART_OBJECTIVE
        );
        const isComplexFlowChartProductiveCombo = (
            selectedItemContentType === qtiTypes.TEXT_ENTRY
            && itemTypeValue?.length && itemTypeValue[0] === customFieldsConstants.ITEM_FLOW_CHART_COMPLETION_PRODUCTIVE
            && itemSubTypeValue?.length && itemSubTypeValue[0] === customFieldsConstants.ITEM_SUB_TYPE_COMPLEX_FLOW_CHART_PRODUCTIVE
        );
        return isComplexFlowChartObjectiveCombo || isComplexFlowChartProductiveCombo;
    },
};

export default customFieldsService;
